import { useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, Tags } from 'lucide-react';
import { TaxonomyPage } from '@/pages/TaxonomyPage';
import { ProposedAdTaxonomyList } from '@/components/knowledge/catalogs/ProposedAdTaxonomyList';
import { AdTaxonomyCanonicalsPanel } from '@/components/knowledge/catalogs/AdTaxonomyCanonicalsPanel';
import { useCatalogsStore } from '@/stores/catalogs';

type Tab = 'canonicals' | 'explorer' | 'proposed';

export function AdTaxonomyCatalogPage() {
  const [tab, setTab] = useState<Tab>('canonicals');
  const { summary } = useCatalogsStore();
  const pending = summary.pending_by_source.ad_taxonomy;

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge/catalogs"
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} />
            Catalogs
          </Link>
          <div className="flex items-center gap-2">
            <Tags size={16} className="text-brand-teal" />
            <span className="text-sm font-semibold text-navy-900">Ad Taxonomy</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <TabButton active={tab === 'canonicals'} onClick={() => setTab('canonicals')}>
            Canonicals
            <span className="ml-1.5 text-[10px] font-bold bg-navy-50 text-navy-700 border border-navy-200 px-1.5 py-0.5 rounded-full">
              Phase 5
            </span>
          </TabButton>
          <TabButton active={tab === 'explorer'} onClick={() => setTab('explorer')}>
            Explorer
          </TabButton>
          <TabButton active={tab === 'proposed'} onClick={() => setTab('proposed')}>
            Proposed Elements
            {pending > 0 && (
              <span className="ml-1.5 text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                {pending}
              </span>
            )}
          </TabButton>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {tab === 'canonicals' ? (
          <div className="p-6">
            <AdTaxonomyCanonicalsPanel />
          </div>
        ) : tab === 'explorer' ? (
          // Render the existing explorer in-place. It provides its own inner
          // "Ad Taxonomy Explorer" sub-title and client selector.
          <div className="p-6">
            <TaxonomyPage />
          </div>
        ) : (
          <div className="p-6">
            <ProposedAdTaxonomyList />
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
        active
          ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark'
          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
      )}
    >
      {children}
    </button>
  );
}
